import type { RouteLocationNormalized, RouteRecordRaw } from "vue-router";
import { routeNames } from "@/router/constants";
import { useIntl } from "@moroku/money-webapp-components";
import { SettingsSection } from "@/features/InternetBanking/constants.ts";

const { t } = useIntl();

export const getInternetBankingRoutes = (): RouteRecordRaw[] => [
  {
    path: "/account-details/:accountId",
    name: routeNames.ACCOUNT_DETAILS_VIEW,
    component: () => import("@/features/InternetBanking/AccountDetailsView.vue"),
    props: (route: RouteLocationNormalized) => ({ initialAccountId: route.params.accountId }),
    meta: {
      title: t("accountDetails_title"),
      headerBottomMargin: false,
    },
  },
  {
    path: "/account-details/:accountId/cards",
    name: routeNames.CARDS_FOR_ACCOUNT,
    component: () => import("@/features/InternetBanking/CardsView.vue"),
    props: (route: RouteLocationNormalized) => ({ applicationId: route.params.accountId }),
    meta: {
      title: t("cards_title"),
    },
  },
  {
    path: "/address-book",
    name: routeNames.ADDRESS_BOOK,
    component: () => import("@/features/InternetBanking/SettingsView.vue"),
    props: () => ({ tab: SettingsSection.AddressBook }),
    meta: {
      title: t("addressBook_title"),
      alias: routeNames.INTERNET_BANKING_SETTINGS,
      headerBottomMargin: false,
    },
  },
  {
    path: "/cards/new-card",
    name: routeNames.CARDS_NEW,
    component: () => import("@/features/InternetBanking/CardNewView.vue"),
    meta: {
      title: t("cards_newCard_title"),
    },
  },
  {
    path: "/card-details/:cardId",
    name: routeNames.CARD_DETAILS,
    component: () => import("@/features/InternetBanking/CardDetailsView.vue"),
    props: route => ({ cardId: route.params.cardId }),
    meta: {
      title: t("card_controls_title"),
      headerBottomMargin: false,
    },
  },
  {
    path: "/financial-overview",
    alias: "/accounts", // The old accounts listing has been merged into the overview
    name: routeNames.FINANCIAL_OVERVIEW,
    component: () => import("@/features/FinancialOverview/FinancialOverviewView.vue"),
    meta: {
      title: t("home_title"),
    },
  },
  {
    path: "/open-account",
    name: routeNames.OPEN_ACCOUNT,
    component: () => import("@/features/InternetBanking/OpenAccountView.vue"),
    meta: {
      title: t("accounts_openAccount_title"),
    },
  },
  {
    path: "/pay-ids",
    name: routeNames.PAY_ID_MANAGE,
    component: () => import("@/features/InternetBanking/SettingsView.vue"),
    props: () => ({ tab: SettingsSection.PayId }),
    meta: {
      title: t("payId_title"),
      alias: routeNames.INTERNET_BANKING_SETTINGS,
      headerBottomMargin: false,
    },
  },
  {
    path: "/business-users",
    name: routeNames.BUSINESS_USERS,
    component: () => import("@/features/InternetBanking/SettingsView.vue"),
    props: () => ({ tab: SettingsSection.BusinessUsers }),
    meta: {
      title: t("authorisedUsers_title"),
      alias: routeNames.INTERNET_BANKING_SETTINGS,
      headerBottomMargin: false,
      requiresBusinessBankingContext: true,
    },
  },
  {
    path: "/pay-id-register",
    name: routeNames.PAY_ID_REGISTER,
    component: () => import("@/features/InternetBanking/PayIdRegisterView.vue"),
    meta: {
      title: t("payId_register_title"),
    },
  },
  {
    path: "/payment",
    name: routeNames.PAYMENT_VIEW,
    component: () => import("@/features/InternetBanking/PaymentView.vue"),
    props: route => ({ paymentType: route.query.type }),
    meta: {
      title: t("payment_title"),
    },
  },
  {
    path: "/transfer",
    name: routeNames.TRANSFER_VIEW,
    component: () => import("@/features/InternetBanking/TransferView.vue"),
    meta: {
      title: t("transfer_title"),
    },
  },
  {
    path: "/banking-settings",
    alias: ["/pay-ids", "/address-book", "/business-users"],
    name: routeNames.INTERNET_BANKING_SETTINGS,
    component: () => import("@/features/InternetBanking/SettingsView.vue"),
    meta: {
      title: t("internetBankingSettings_title"),
      headerBottomMargin: false,
    },
  },
  {
    path: "/onboarding-required",
    name: routeNames.REQUIRES_ONBOARDING_VIEW,
    component: () => import("@/features/Onboarding/RequiresOnboardingView.vue"),
    meta: {
      title: t("requiresOnboarding_title"),
    },
  },
];
